// PhotographyTestimonials.js - Photography Testimonials section component with slider
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a photography-themed Testimonials section with slider functionality
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the testimonials are being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The testimonials elements
 */
export const createPhotographyTestimonialsElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const testimonialsSectionClass = 'photography-testimonials-section';
  const testimonialsPatternOverlayClass = 'photography-testimonials-pattern-overlay';
  const testimonialsContainerClass = 'photography-testimonials-container';
  const testimonialsHeaderClass = 'photography-testimonials-header';
  const testimonialsTitleClass = 'photography-testimonials-title';
  const testimonialsSubtitleClass = 'photography-testimonials-subtitle';
  const testimonialsSliderClass = 'photography-testimonials-slider';
  const testimonialsSlideClass = 'photography-testimonials-slide';
  const testimonialsCardClass = 'photography-testimonials-card';
  const testimonialsControlsClass = 'photography-testimonials-controls';
  const testimonialsQuoteClass = 'photography-testimonials-quote';
  const testimonialsRatingClass = 'photography-testimonials-rating';
  const testimonialsAuthorContainerClass = 'photography-testimonials-author-container';
  const testimonialsAvatarClass = 'photography-testimonials-avatar';
  const testimonialsAuthorInfoClass = 'photography-testimonials-author-info';
  const testimonialsNameClass = 'photography-testimonials-name';
  const testimonialsRoleClass = 'photography-testimonials-role';
  const testimonialsProjectClass = 'photography-testimonials-project';

  // Desktop Styles - Photography theme with elegant dark design
  const testimonialsSectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#0f0f0f',
    background: `
      radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(212, 175, 55, 0.06) 0%, transparent 50%),
      linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 25%, #2d2d2d 50%, #1a1a1a 75%, #0f0f0f 100%)
    `,
    color: '#f5f5f5',
    width: '100%',
    overflow: 'hidden'
  };

  const testimonialsPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.03) 1px, transparent 1px),
      radial-gradient(circle at 75% 75%, rgba(212, 175, 55, 0.03) 1px, transparent 1px)
    `,
    backgroundSize: '80px 80px, 120px 120px',
    backgroundPosition: '0 0, 60px 60px',
    zIndex: '0',
    opacity: '0.4'
  };

  const testimonialsContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const testimonialsHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const testimonialsTitleStyles = {
    fontSize: '56px',
    fontWeight: '300',
    color: '#f5f5f5',
    marginBottom: '24px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '2px',
    lineHeight: '1.1',
    position: 'relative'
  };

  const testimonialsSubtitleStyles = {
    fontSize: '20px',
    color: 'rgba(245, 245, 245, 0.8)',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '300'
  };

  const testimonialsSliderStyles = {
    position: 'relative',
    width: '100%',
    height: '500px',
    overflow: 'hidden',
    backgroundColor: 'transparent'
  };

  const testimonialsSlideStyles = {
    flex: '0 0 100%',
    height: '100%',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: '20px'
  };

  const testimonialsCardStyles = {
    width: '100%',
    maxWidth: '800px',
    height: '100%',
    backgroundColor: 'rgba(26, 26, 26, 0.8)',
    borderRadius: '0',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    padding: '60px 50px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    backdropFilter: 'blur(10px)',
    position: 'relative',
    overflow: 'hidden'
  };

  const testimonialsCardHoverStyles = {
    backgroundColor: 'rgba(26, 26, 26, 0.9)',
    border: '1px solid rgba(212, 175, 55, 0.4)',
    transform: 'translateY(-5px)',
    boxShadow: `
      0 25px 50px rgba(0, 0, 0, 0.6),
      0 0 30px rgba(212, 175, 55, 0.15)
    `
  };

  const testimonialsControlsStyles = {
    display: 'flex',
    gap: '20px',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '40px'
  };

  // Desktop styles for testimonial content
  const testimonialsQuoteStyles = {
    fontSize: '24px',
    lineHeight: '1.6',
    color: '#f5f5f5',
    marginBottom: '30px',
    fontStyle: 'italic',
    fontFamily: '"Playfair Display", serif',
    fontWeight: '300',
    position: 'relative',
    paddingLeft: '30px',
    borderLeft: '3px solid #d4af37',
    maxWidth: '600px'
  };

  const testimonialsRatingStyles = {
    display: 'flex',
    gap: '4px',
    marginBottom: '30px',
    color: '#d4af37',
    fontSize: '20px',
    justifyContent: 'center'
  };

  const testimonialsAuthorContainerStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    justifyContent: 'center'
  };

  const testimonialsAvatarStyles = {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid #d4af37',
    boxShadow: '0 5px 15px rgba(212, 175, 55, 0.3)'
  };

  const testimonialsAuthorInfoStyles = {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left'
  };

  const testimonialsNameStyles = {
    fontSize: '20px',
    fontWeight: '400',
    color: '#f5f5f5',
    marginBottom: '4px',
    marginTop: '0',
    fontFamily: '"Playfair Display", serif'
  };

  const testimonialsRoleStyles = {
    fontSize: '14px',
    color: '#d4af37',
    marginBottom: '2px',
    marginTop: '0',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  const testimonialsProjectStyles = {
    fontSize: '12px',
    color: 'rgba(245, 245, 245, 0.7)',
    marginTop: '0',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '300'
  };

  // Tablet Styles
  const testimonialsTabletStyles = {
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const testimonialsTitleTabletStyles = {
    fontSize: '48px'
  };

  const testimonialsSubtitleTabletStyles = {
    fontSize: '18px'
  };

  const testimonialsSliderTabletStyles = {
    height: '450px'
  };

  const testimonialsCardTabletStyles = {
    padding: '50px 40px'
  };

  // Mobile Styles
  const testimonialsMobileStyles = {
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const testimonialsTitleMobileStyles = {
    fontSize: '40px'
  };

  const testimonialsSubtitleMobileStyles = {
    fontSize: '16px'
  };

  const testimonialsSliderMobileStyles = {
    height: '400px'
  };

  const testimonialsCardMobileStyles = {
    height: 'auto',
    padding: '40px 30px'
  };

  // Mobile styles for testimonial content
  const testimonialsQuoteMobileStyles = {
    fontSize: '18px',
    lineHeight: '1.5',
    marginBottom: '20px',
    paddingLeft: '20px',
    maxWidth: '100%'
  };

  const testimonialsRatingMobileStyles = {
    fontSize: '18px',
    marginBottom: '20px'
  };

  const testimonialsAuthorContainerMobileStyles = {
    flexDirection: 'column',
    gap: '15px',
    textAlign: 'center'
  };

  const testimonialsAvatarMobileStyles = {
    width: '60px',
    height: '60px'
  };

  const testimonialsNameMobileStyles = {
    fontSize: '18px',
    marginBottom: '4px'
  };

  const testimonialsRoleMobileStyles = {
    fontSize: '13px',
    marginBottom: '2px'
  };

  const testimonialsProjectMobileStyles = {
    fontSize: '11px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(testimonialsSectionClass, testimonialsSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsPatternOverlayClass, testimonialsPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsContainerClass, testimonialsContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsHeaderClass, testimonialsHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsTitleClass, testimonialsTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsSubtitleClass, testimonialsSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsSliderClass, testimonialsSliderStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsSlideClass, testimonialsSlideStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsCardClass, testimonialsCardStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsControlsClass, testimonialsControlsStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsQuoteClass, testimonialsQuoteStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsRatingClass, testimonialsRatingStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsAuthorContainerClass, testimonialsAuthorContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsAvatarClass, testimonialsAvatarStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsAuthorInfoClass, testimonialsAuthorInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsNameClass, testimonialsNameStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsRoleClass, testimonialsRoleStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialsProjectClass, testimonialsProjectStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${testimonialsCardClass}--hover`, testimonialsCardHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(testimonialsSectionClass, testimonialsTabletStyles);
    newRegistry[DEVICES.TABLET].set(testimonialsTitleClass, testimonialsTitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(testimonialsSubtitleClass, testimonialsSubtitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(testimonialsSliderClass, testimonialsSliderTabletStyles);
    newRegistry[DEVICES.TABLET].set(testimonialsCardClass, testimonialsCardTabletStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(testimonialsSectionClass, testimonialsMobileStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialsTitleClass, testimonialsTitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialsSubtitleClass, testimonialsSubtitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialsSliderClass, testimonialsSliderMobileStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialsCardClass, testimonialsCardMobileStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialsQuoteClass, testimonialsQuoteMobileStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialsRatingClass, testimonialsRatingMobileStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialsAuthorContainerClass, testimonialsAuthorContainerMobileStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialsAvatarClass, testimonialsAvatarMobileStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialsNameClass, testimonialsNameMobileStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialsRoleClass, testimonialsRoleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(testimonialsProjectClass, testimonialsProjectMobileStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    testimonialsSectionClass,
    testimonialsPatternOverlayClass,
    testimonialsContainerClass,
    testimonialsHeaderClass,
    testimonialsTitleClass,
    testimonialsSubtitleClass,
    testimonialsSliderClass,
    testimonialsSlideClass,
    testimonialsCardClass,
    testimonialsControlsClass,
    testimonialsQuoteClass,
    testimonialsRatingClass,
    testimonialsAuthorContainerClass,
    testimonialsAvatarClass,
    testimonialsAuthorInfoClass,
    testimonialsNameClass,
    testimonialsRoleClass,
    testimonialsProjectClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const patternOverlayId = Date.now() + 1;
  const containerId = Date.now() + 2;
  const headerId = Date.now() + 3;
  const titleId = Date.now() + 4;
  const subtitleId = Date.now() + 5;
  const sliderId = Date.now() + 6;
  const maskId = Date.now() + 7;
  const leftArrowId = Date.now() + 8;
  const rightArrowId = Date.now() + 9;
  const controlsId = Date.now() + 10;
  const autoplayToggleId = Date.now() + 11;
  const loopToggleId = Date.now() + 12;

  // Slide IDs
  const slide1Id = Date.now() + 100;
  const slide2Id = Date.now() + 200;
  const slide3Id = Date.now() + 300;
  const slide4Id = Date.now() + 400;

  // Card IDs
  const card1Id = slide1Id + 1000;
  const card2Id = slide2Id + 1000;
  const card3Id = slide3Id + 1000;
  const card4Id = slide4Id + 1000;

  // Photography testimonials data
  const testimonials = [
    {
      name: 'Emma & James Wilson',
      role: 'Wedding Clients',
      quote: 'Our wedding photos exceeded every expectation. The way they captured the emotion and beauty of our special day was absolutely magical. Every image tells our love story perfectly.',
      avatar: 'https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=150&h=150&fit=crop&crop=center',
      rating: 5,
      project: 'Garden Wedding Photography'
    },
    {
      name: 'Sarah Mitchell',
      role: 'Portrait Client',
      quote: 'The professional headshots transformed my business presence completely. The attention to detail and ability to capture my personality in each shot was remarkable.',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=center',
      rating: 5,
      project: 'Executive Portrait Session'
    },
    {
      name: 'David Chen',
      role: 'Corporate Client',
      quote: 'Outstanding commercial photography that elevated our brand image. The creative vision and technical expertise delivered results beyond our expectations.',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=center',
      rating: 5,
      project: 'Corporate Brand Photography'
    },
    {
      name: 'Lisa & Michael Rodriguez',
      role: 'Family Clients',
      quote: 'Beautiful family portraits that we will treasure forever. The photographer made everyone feel comfortable and captured genuine moments of joy and connection.',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=center',
      rating: 5,
      project: 'Family Portrait Session'
    }
  ];

  // Create slide function
  const createSlide = (slideId, cardId, index) => {
    return {
      id: slideId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Testimonial Slide ${index}`,
      parentId: maskId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialsSlideClass,
      sliderRole: 'slide',
      styles: {
        flex: '0 0 100%',
        height: '100%'
      },
      text: '',
      children: [cardId],
      attributes: {
        'data-element': 'testimonial-slide',
        'data-slide-index': index - 1
      }
    };
  };

  // Create testimonial card function
  const createTestimonialCard = (id, parentId, testimonialData) => {
    const uniqueBase = id * 100;
    
    const quoteId = uniqueBase + 10;
    const ratingId = uniqueBase + 20;
    const authorContainerId = uniqueBase + 30;
    const avatarId = uniqueBase + 31;
    const authorInfoId = uniqueBase + 32;
    const nameId = uniqueBase + 33;
    const roleId = uniqueBase + 34;
    const projectId = uniqueBase + 35;
    
    const cardElement = {
      id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Testimonial Card - ${testimonialData.name}`,
      parentId: parentId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialsCardClass,
      styles: {},
      text: '',
      children: [quoteId, ratingId, authorContainerId],
      attributes: {
        'data-element': 'testimonial-card',
        'data-testimonial-name': testimonialData.name
      }
    };
    
    const childElements = [
      // Quote
      {
        id: quoteId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'blockquote',
        name: 'Testimonial Quote',
        parentId: id,
        hierarchyName: getHierarchyName('blockquote'),
        className: testimonialsQuoteClass,
        styles: {},
        text: `"${testimonialData.quote}"`,
        children: [],
        attributes: {
          'data-element': 'testimonial-quote'
        }
      },
      // Rating
      {
        id: ratingId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: 'Testimonial Rating',
        parentId: id,
        hierarchyName: getHierarchyName('div'),
        className: testimonialsRatingClass,
        styles: {},
        text: '★'.repeat(testimonialData.rating),
        children: [],
        attributes: {
          'data-element': 'testimonial-rating',
          'data-rating': testimonialData.rating
        }
      },
      // Author Container
      {
        id: authorContainerId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: 'Author Container',
        parentId: id,
        hierarchyName: getHierarchyName('div'),
        className: testimonialsAuthorContainerClass,
        styles: {},
        text: '',
        children: [avatarId, authorInfoId],
        attributes: {
          'data-element': 'testimonial-author-container'
        }
      },
      // Avatar
      {
        id: avatarId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'image',
        name: 'Author Avatar',
        parentId: authorContainerId,
        hierarchyName: getHierarchyName('image'),
        className: testimonialsAvatarClass,
        styles: {},
        src: testimonialData.avatar,
        children: [],
        attributes: {
          'data-element': 'testimonial-avatar',
          'alt': testimonialData.name
        }
      },
      // Author Info
      {
        id: authorInfoId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: 'Author Info',
        parentId: authorContainerId,
        hierarchyName: getHierarchyName('div'),
        className: testimonialsAuthorInfoClass,
        styles: {},
        text: '',
        children: [nameId, roleId, projectId],
        attributes: {
          'data-element': 'testimonial-author-info'
        }
      },
      // Name
      {
        id: nameId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'h4',
        name: 'Author Name',
        parentId: authorInfoId,
        hierarchyName: getHierarchyName('h4'),
        className: testimonialsNameClass,
        styles: {},
        text: testimonialData.name,
        children: [],
        attributes: {
          'data-element': 'testimonial-author-name'
        }
      },
      // Role
      {
        id: roleId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'p',
        name: 'Author Role',
        parentId: authorInfoId,
        hierarchyName: getHierarchyName('p'),
        className: testimonialsRoleClass,
        styles: {},
        text: testimonialData.role,
        children: [],
        attributes: {
          'data-element': 'testimonial-author-role'
        }
      },
      // Project
      {
        id: projectId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'p',
        name: 'Project Type',
        parentId: authorInfoId,
        hierarchyName: getHierarchyName('p'),
        className: testimonialsProjectClass,
        styles: {},
        text: testimonialData.project,
        children: [],
        attributes: {
          'data-element': 'testimonial-project'
        }
      }
    ];
    
    return {
      card: cardElement,
      children: childElements
    };
  };

  // Create slides
  const slide1 = createSlide(slide1Id, card1Id, 1);
  const slide2 = createSlide(slide2Id, card2Id, 2);
  const slide3 = createSlide(slide3Id, card3Id, 3);
  const slide4 = createSlide(slide4Id, card4Id, 4);

  // Create cards
  const card1Result = createTestimonialCard(card1Id, slide1Id, testimonials[0]);
  const card2Result = createTestimonialCard(card2Id, slide2Id, testimonials[1]);
  const card3Result = createTestimonialCard(card3Id, slide3Id, testimonials[2]);
  const card4Result = createTestimonialCard(card4Id, slide4Id, testimonials[3]);

  // Create arrow function
  const createArrow = (id, direction) => {
    const imageId = id + 1000;
    
    return {
      id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      sliderRole: 'arrow',
      direction: direction,
      text: '',
      styles: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '50px',
        height: '50px',
        backgroundColor: 'rgba(26, 26, 26, 0.9)',
        border: '1px solid rgba(212, 175, 55, 0.3)',
        borderRadius: '50%',
        cursor: 'pointer',
        zIndex: 5,
        textDecoration: 'none',
        transition: 'all 0.3s ease',
        backdropFilter: 'blur(10px)',
        left: direction === 'left' ? '20px' : 'auto',
        right: direction === 'right' ? '20px' : 'auto'
      },
      children: [imageId],
      parentId: sliderId,
      hierarchyName: getHierarchyName('link'),
      interactions: {
        events: [
          {
            id: Date.now() + (direction === 'left' ? 1 : 2),
            type: 'click',
            action: 'custom',
            label: `${direction === 'left' ? 'Previous' : 'Next'} testimonial`,
            value: {
              script: `
                const slider = event.target.closest('[data-element-type="slider"]');
                if (!slider) return;
                
                const mask = slider.querySelector('[data-slider-role="mask"]');
                if (!mask) return;
                
                let slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || 1);
                let slidesPerScroll = parseInt(slider.getAttribute('data-slides-per-scroll') || 1);
                const loopEnabled = slider.getAttribute('data-loop') === 'true';
                
                const slides = mask.children.length;
                let currentSlide = parseInt(mask.getAttribute('data-current-slide') || 0);
                
                if ('${direction}' === 'left') {
                  if (currentSlide === 0 && loopEnabled) {
                    currentSlide = slides - slidesPerView;
                  } else {
                    currentSlide = Math.max(0, currentSlide - slidesPerScroll);
                  }
                } else {
                  if (currentSlide >= slides - slidesPerView && loopEnabled) {
                    currentSlide = 0;
                  } else {
                    currentSlide = Math.min(slides - slidesPerView, currentSlide + slidesPerScroll);
                  }
                }
                
                mask.style.transition = 'transform 0.5s ease-in-out';
                mask.style.transform = \`translateX(-\${currentSlide * (100/slidesPerView)}%)\`;
                mask.setAttribute('data-current-slide', currentSlide);
                
                event.preventDefault();
              `
            }
          }
        ]
      },
      attributes: {
        'href': '#',
        'aria-label': direction === 'left' ? 'Previous testimonial' : 'Next testimonial',
        'data-direction': direction
      }
    };
  };

  // Create arrow images
  const createArrowImage = (id, parentId, direction) => ({
    id,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'image',
    text: '',
    styles: {
      width: '24px',
      height: '24px',
      objectFit: 'contain',
      filter: 'brightness(0) saturate(100%) invert(85%) sepia(6%) saturate(331%) hue-rotate(314deg) brightness(96%) contrast(93%)'
    },
    src: direction === 'left' 
      ? 'https://cdn-icons-png.flaticon.com/512/130/130882.png'
      : 'https://cdn-icons-png.flaticon.com/512/130/130884.png',
    children: [],
    parentId: parentId,
    hierarchyName: getHierarchyName('image')
  });

  const leftArrow = createArrow(leftArrowId, 'left');
  const rightArrow = createArrow(rightArrowId, 'right');
  const leftArrowImage = createArrowImage(leftArrowId + 1000, leftArrowId, 'left');
  const rightArrowImage = createArrowImage(rightArrowId + 1000, rightArrowId, 'right');

  // Create all elements
  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Photography Testimonials Section',
      parentId: null,
      className: testimonialsSectionClass,
      styles: {},
      children: [patternOverlayId, containerId],
      text: '',
      attributes: {
        'data-element': 'testimonials-section',
        'data-component': 'section',
        'data-section-type': 'testimonials',
        'data-page-id': currentPageId
      }
    },
    // Pattern Overlay
    {
      id: patternOverlayId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Testimonials Pattern Overlay',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialsPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'testimonials-pattern-overlay'
      }
    },
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Testimonials Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialsContainerClass,
      styles: {},
      children: [headerId, sliderId, controlsId],
      text: '',
      attributes: {
        'data-element': 'testimonials-container'
      }
    },
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Testimonials Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialsHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'testimonials-header'
      }
    },
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Testimonials Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: testimonialsTitleClass,
      styles: {},
      children: [],
      text: 'Client Testimonials',
      attributes: {
        'data-element': 'testimonials-title'
      }
    },
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Testimonials Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: testimonialsSubtitleClass,
      styles: {},
      children: [],
      text: 'Hear from our satisfied clients about their photography experience. Each testimonial reflects our commitment to capturing life\'s most precious moments with artistry and professionalism.',
      attributes: {
        'data-element': 'testimonials-subtitle'
      }
    },
    // Slider Container
    {
      id: sliderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'slider',
      name: 'Testimonials Slider',
      parentId: containerId,
      hierarchyName: getHierarchyName('slider'),
      className: testimonialsSliderClass,
      sliderRole: 'container',
      styles: {},
      children: [maskId, leftArrowId, rightArrowId],
      text: '',
      slidesPerView: 1,
      slidesPerScroll: 1,
      deviceSettings: {
        desktop: { slidesPerView: 2, slidesPerScroll: 1 },
        tablet: { slidesPerView: 1, slidesPerScroll: 1 },
        mobile: { slidesPerView: 1, slidesPerScroll: 1 }
      },
      attributes: {
        'data-element': 'testimonials-slider',
        'data-element-type': 'slider',
        'data-loop': 'true',
        'data-autoplay': 'true',
        'data-delay': '6000'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 100,
            type: 'custom',
            action: 'custom',
            label: 'Initialize testimonials slider',
            value: {
              script: `
                let isDragging = false;
                let startPosition = 0;
                let currentTranslate = 0;
                let prevTranslate = 0;
                let autoplayInterval = null;

                const slider = event.currentTarget;
                const mask = slider.querySelector('[data-slider-role="mask"]');
                if (!mask) return;

                mask.addEventListener('mousedown', dragStart);
                mask.addEventListener('touchstart', dragStart, { passive: true });
                mask.addEventListener('mouseup', dragEnd);
                mask.addEventListener('touchend', dragEnd);
                mask.addEventListener('mouseleave', dragEnd);
                mask.addEventListener('mousemove', drag);
                mask.addEventListener('touchmove', drag, { passive: true });

                function getSliderSettings() {
                  return {
                    slidesPerView: parseInt(slider.getAttribute('data-slides-per-view') || '1'),
                    slidesPerScroll: parseInt(slider.getAttribute('data-slides-per-scroll') || '1'),
                    loopEnabled: slider.getAttribute('data-loop') === 'true',
                    autoplayEnabled: slider.getAttribute('data-autoplay') === 'true',
                    autoplayDelay: parseInt(slider.getAttribute('data-delay') || '6000')
                  };
                }

                function dragStart(e) {
                  e.preventDefault();
                  if (e.type === 'touchstart') {
                    startPosition = e.touches[0].clientX;
                  } else {
                    startPosition = e.clientX;
                  }
                  
                  if (autoplayInterval) {
                    clearInterval(autoplayInterval);
                  }
                  
                  isDragging = true;
                  
                  const style = window.getComputedStyle(mask);
                  const matrix = new WebKitCSSMatrix(style.transform);
                  prevTranslate = matrix.m41;
                  
                  cancelAnimationFrame(animationID);
                }

                function drag(e) {
                  if (!isDragging) return;
                  
                  let currentPosition;
                  if (e.type === 'touchmove') {
                    currentPosition = e.touches[0].clientX;
                  } else {
                    currentPosition = e.clientX;
                  }
                  
                  const diff = currentPosition - startPosition;
                  currentTranslate = prevTranslate + diff;
                  
                  mask.style.transition = 'none';
                  mask.style.transform = \`translateX(\${currentTranslate}px)\`;
                }

                function dragEnd() {
                  if (!isDragging) return;
                  
                  isDragging = false;
                  mask.style.transition = 'transform 0.3s ease-out';
                  
                  const settings = getSliderSettings();
                  const { slidesPerView, slidesPerScroll, loopEnabled } = settings;
                  const slides = mask.children.length;
                  
                  const maskWidth = mask.offsetWidth;
                  const slideWidth = maskWidth / slidesPerView;
                  
                  let currentSlide = parseInt(mask.getAttribute('data-current-slide') || '0');
                  
                  const moveThreshold = slideWidth * 0.2;
                  const movement = Math.abs(currentTranslate - prevTranslate);
                  
                  if (movement > moveThreshold) {
                    if (currentTranslate > prevTranslate) {
                      if (currentSlide === 0 && loopEnabled) {
                        currentSlide = slides - slidesPerView;
                      } else {
                        currentSlide = Math.max(0, currentSlide - slidesPerScroll);
                      }
                    } else {
                      if (currentSlide >= slides - slidesPerView && loopEnabled) {
                        currentSlide = 0;
                      } else {
                        currentSlide = Math.min(slides - slidesPerView, currentSlide + slidesPerScroll);
                      }
                    }
                  }
                  
                  goToSlide(currentSlide);
                  initAutoplay();
                }

                function goToSlide(slideIndex) {
                  const settings = getSliderSettings();
                  const { slidesPerView } = settings;
                  
                  mask.style.transform = \`translateX(-\${slideIndex * (100 / slidesPerView)}%)\`;
                  mask.setAttribute('data-current-slide', slideIndex);
                }

                function initAutoplay() {
                  const settings = getSliderSettings();
                  const { autoplayEnabled, autoplayDelay, loopEnabled } = settings;
                  
                  if (autoplayInterval) {
                    clearInterval(autoplayInterval);
                    autoplayInterval = null;
                  }
                  
                  if (autoplayEnabled) {
                    autoplayInterval = setInterval(() => {
                      const slides = mask.children.length;
                      const slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || '1');
                      const slidesPerScroll = parseInt(slider.getAttribute('data-slides-per-scroll') || '1');
                      let currentSlide = parseInt(mask.getAttribute('data-current-slide') || '0');
                      
                      if (currentSlide >= slides - slidesPerView) {
                        if (loopEnabled) {
                          currentSlide = 0;
                        } else {
                          clearInterval(autoplayInterval);
                          return;
                        }
                      } else {
                        currentSlide += slidesPerScroll;
                      }
                      
                      goToSlide(currentSlide);
                    }, autoplayDelay);
                  }
                }

                initAutoplay();

                window.addEventListener('resize', () => {
                  dragEnd();
                  setTimeout(initAutoplay, 100);
                });
              `
            }
          }
        ]
      }
    },
    // Slider Mask
    {
      id: maskId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Testimonials Slider Mask',
      parentId: sliderId,
      hierarchyName: getHierarchyName('div'),
      sliderRole: 'mask',
      styles: {
        position: 'absolute',
        display: 'flex',
        width: '100%',
        height: '100%',
        transition: 'transform 0.5s ease-in-out',
        cursor: 'grab'
      },
      children: [slide1Id, slide2Id, slide3Id, slide4Id],
      text: '',
      attributes: {
        'data-element': 'slider-mask',
        'data-current-slide': '0',
        'data-slides-count': '4'
      }
    },
    leftArrow,
    rightArrow,
    // Controls
    {
      id: controlsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Testimonials Controls',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialsControlsClass,
      styles: {},
      children: [autoplayToggleId, loopToggleId],
      attributes: {
        'data-element': 'testimonials-controls'
      }
    },
    // Autoplay toggle
    {
      id: autoplayToggleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Autoplay Toggle',
      parentId: controlsId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      },
      children: [],
      text: '',
      attributes: {
        'data-element': 'autoplay-toggle'
      },
      innerHTML: `
        <label for="autoplay-toggle-${sectionId}" style="font-size: 14px; color: #f5f5f5; font-family: 'Inter', sans-serif;">Autoplay</label>
        <input type="checkbox" id="autoplay-toggle-${sectionId}" checked style="cursor: pointer;" onchange="
          const slider = document.querySelector('#${sliderId}');
          if (slider) {
            slider.setAttribute('data-autoplay', this.checked ? 'true' : 'false');
          }
        ">
      `
    },
    // Loop toggle
    {
      id: loopToggleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Loop Toggle',
      parentId: controlsId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      },
      children: [],
      text: '',
      attributes: {
        'data-element': 'loop-toggle'
      },
      innerHTML: `
        <label for="loop-toggle-${sectionId}" style="font-size: 14px; color: #f5f5f5; font-family: 'Inter', sans-serif;">Loop</label>
        <input type="checkbox" id="loop-toggle-${sectionId}" checked style="cursor: pointer;" onchange="
          const slider = document.querySelector('#${sliderId}');
          if (slider) {
            slider.setAttribute('data-loop', this.checked ? 'true' : 'false');
          }
        ">
      `
    },
    slide1,
    slide2,
    slide3,
    slide4,
    card1Result.card,
    card2Result.card,
    card3Result.card,
    card4Result.card,
    leftArrowImage,
    rightArrowImage,
    ...card1Result.children,
    ...card2Result.children,
    ...card3Result.children,
    ...card4Result.children
  ];

  return {
    testimonialsSection: newElements[0],
    elements: newElements
  };
}; 